import axios from 'axios'

// Create axios instance with base configuration
const api = axios.create({
	baseURL: '/api',
	timeout: 30000,
	headers: {
		'Content-Type': 'application/json',
	},
})

// Request interceptor for adding auth tokens
api.interceptors.request.use(
	(config) => {
		// Add auth token if available
		const token = localStorage.getItem('auth_token')
		if (token) {
			config.headers.Authorization = `Bearer ${token}`
		}
		return config
	},
	(error) => {
		return Promise.reject(error)
	}
)

// Response interceptor for error handling.
// We do NOT redirect to '/login' (there is no such route in this SPA) - that
// used to bounce visitors off public pages whenever an upstream API call
// returned 401 (e.g. dev environments without the X-API-Key proxy header).
// We just clear any stored auth token and let the caller surface the error.
api.interceptors.response.use(
	(response) => response,
	(error) => {
		if (error.response?.status === 401) {
			localStorage.removeItem('auth_token')
		}
		return Promise.reject(error)
	}
)

// NFPT API endpoints
export const nfptApi = {
	// Prepare an inscription server-side: uploads media to IPFS, builds the
	// canonical `t:nfpt` memo and hands back everything the caller needs to
	// send the transaction themselves (no private key ever leaves the
	// browser).
	prepareInscription: (data: {
		file: string
		name: string
		description?: string
		data_format?: string
		category?: string
		external_url?: string
		attributes?: Array<{ trait_type: string; value: string | number }>
		collection_id?: string
		inscription_zec?: number
		registry_fee_zec?: number
		creator_public_key?: string
	}) => api.post('/nfpt/prepare-inscription', data),

	// Legacy server-signed inscription (still wired up but no longer used by
	// the public Create page – kept for back-end automation flows).
	inscribe: (data: {
		file?: string
		name?: string
		description?: string
		data_format?: string
		registry_z_address?: string
		inscription_zec: number
		registry_fee_zec?: number
		creator_private_key: string
	}) => api.post('/nfpt/inscribe', data),

	// Prepare an NFPT transfer server-side: looks up the original inscription
	// memo, builds the `t:nfpt_transfer` memo and returns the recipient +
	// amount the caller has to send themselves. No private key required.
	prepareTransfer: (data: {
		nfpt_cid: string
		recipient_z_address: string
		zec_amount?: number
	}) => api.post('/nfpt/prepare-transfer', data),

	// Legacy server-signed transfer (still wired up but no longer used by
	// public flows – kept for back-end automation).
	transfer: (data: {
		recipient_z_address: string
		zec_amount: number
		memo: string
		sender_private_key: string
	}) => api.post('/nfpt/transfer', data),

	// Verify NFPT authenticity
	verify: (params: {
		cid?: string
		z_address?: string
		view_key?: string
		registry_z_address: string
		creator_public_key: string
	}) => api.get('/nfpt/verify', { params }),

	// List NFPTs
	list: (params: {
		z_address?: string
		registry_z_address?: string
		view_key?: string
		page?: number
		limit?: number
	}) => api.get('/nfpt/list', { params }),

	// Get NFPT details by CID
	getDetails: (cid: string) => api.get(`/nfpt/${cid}`),
}

// Collections API endpoints
export const collectionsApi = {
	// Create a new collection
	create: (data: {
		name: string
		description: string
		banner_image?: string
		total_supply: number
		categories?: string[]
		website?: string
		base_url?: string
		base_price?: number
		creator_private_key: string
		registry_z_address?: string
		default_royalties?: Array<{
			address: string
			amount: number
		}>
		creation_fee_zec?: number
	}) => api.post('/collections', data),

	// Get all collections
	list: (params: {
		category?: string
		verified?: boolean
		page?: number
		limit?: number
		sort?: string
	}) => api.get('/collections', { params }),

	// Get collection by ID
	getById: (id: string) => api.get(`/collections/${id}`),

	// Update collection
	update: (id: string, data: any) => api.put(`/collections/${id}`, data),

	// Get collection statistics
	getStats: (id: string) => api.get(`/collections/${id}/stats`),
}

// Auto-purchase API endpoints
export const autoPurchaseApi = {
	// Create auto-purchase rule
	createRule: (data: {
		name: string
		max_price: number
		collection_id?: string
		traits?: Record<string, any>
		budget_limit: number
		is_active: boolean
		user_z_address: string
		user_private_key: string
	}) => api.post('/auto-purchase/rules', data),

	// Get user's auto-purchase rules
	getRules: (params: {
		user_z_address: string
		page?: number
		limit?: number
	}) => api.get('/auto-purchase/rules', { params }),

	// Update auto-purchase rule
	updateRule: (id: string, data: any) => api.put(`/auto-purchase/rules/${id}`, data),

	// Delete auto-purchase rule
	deleteRule: (id: string) => api.delete(`/auto-purchase/rules/${id}`),

	// Get purchase history
	getHistory: (params: {
		user_z_address: string
		page?: number
		limit?: number
	}) => api.get('/auto-purchase/history', { params }),

	// Get rule statistics
	getRuleStats: (id: string) => api.get(`/auto-purchase/rules/${id}/stats`),
}

// Health check
export const healthApi = {
	check: () => api.get('/health'),
}

// Marketplace API endpoints
export const marketplaceApi = {
	// Get marketplace listings
	getListings: (params: {
		collection_id?: string
		category?: string
		min_price?: number
		max_price?: number
		verified?: boolean
		sort?: string
		order?: string
		limit?: number
		offset?: number
	}) => api.get('/marketplace/listings', { params }),

	// Get listing details by CID
	getListingDetails: (cid: string) => api.get(`/marketplace/listings/${cid}`),

	// Get marketplace statistics
	getStats: () => api.get('/marketplace/stats'),

	// Owner-driven "list for sale" flow. Mirrors prepare-mint /
	// prepare-transfer: the server builds the canonical `nfpt_list` memo +
	// target shielded address; the client signs & broadcasts the tx itself
	// (in-browser WebZjs or copy-paste-into-wallet); then it posts the txid
	// back via recordListing so the DB stays in sync. No private key is
	// ever transmitted.
	prepareListing: (data: {
		nfpt_cid: string
		sale_price_zec: number
		owner_z_address: string
		expiry_blocks?: number
	}) => api.post('/marketplace/prepare-listing', data),

	recordListing: (data: {
		nfpt_cid: string
		txid: string
		memo: string
		owner_z_address: string
		block_height?: number
	}) => api.post('/marketplace/record-listing', data),

	// Symmetric "remove my listing" pair.
	prepareCancelListing: (data: {
		nfpt_cid: string
		owner_z_address: string
	}) => api.post('/marketplace/prepare-cancel-listing', data),

	recordCancelListing: (data: {
		nfpt_cid: string
		txid: string
		owner_z_address: string
	}) => api.post('/marketplace/record-cancel-listing', data),

	// Buyer-driven purchase flow. The seller's UA is revealed only at
	// prepare time (so the buyer can pay them) — public listings never
	// echo it.
	preparePurchase: (data: {
		nfpt_cid: string
		buyer_z_address: string
	}) => api.post('/marketplace/prepare-purchase', data),

	recordPurchase: (data: {
		nfpt_cid: string
		txid: string
		memo: string
		buyer_z_address: string
		amount_zec: number
	}) => api.post('/marketplace/record-purchase', data),

	// "Show me what I own". POST-only so the UA doesn't leak into nginx
	// access logs.
	getOwnedItems: (data: {
		owner_z_address: string
		limit?: number
	}) => api.post('/marketplace/owned-items', data)
}

// ZINC Zones (ZINC-3). Canonical paths are /zones/*; the server keeps
// /zns/* mounted as a legacy alias for old clients and published links.
export const znsApi = {
	registerDomain: (domain: string) => api.post('/zones/domains', { domain }),
	refreshDomain: (domain: string) => api.post(`/zones/domains/${domain}/refresh`),
	getDomain: (domain: string, params?: { type?: string; refresh?: boolean }) =>
		api.get(`/zones/domains/${domain}`, { params }),
	listDomains: () => api.get('/zones/domains'),
}

// ZINC-4 EVM bridge (wrapped inscriptions).
export const bridgeApi = {
	// Public bridge status: chain, contract, custody disclosure
	// (the custody UFVK is published on purpose — proof of reserve).
	getStatus: () => api.get('/bridge/status'),

	// Public wrap ledger (newest first).
	listWraps: (params?: { limit?: number }) => api.get('/bridge/wraps', { params }),

	// Full lifecycle history for one wrapped token id.
	getWrap: (tokenId: string) => api.get(`/bridge/wraps/${tokenId}`),

	// Owner announces a wrap; response carries the custody UA + exact memo
	// to broadcast. Mirrors prepare-transfer: no private key transmitted.
	prepareWrap: (data: {
		cid: string
		sender_z_address: string
		evm_recipient: string
		net?: string
	}) => api.post('/bridge/prepare-wrap', data),

	// ZINC-5 inbound ledger (EVM NFTs vaulted into shadow inscriptions).
	listInbound: (params?: { limit?: number }) => api.get('/bridge/inbound', { params }),

	// Sanitised origin-NFT preview (server-fetched metadata + image as a
	// data: URI — the browser never contacts the origin host).
	previewInbound: (params: { collection: string; token_id: string }) =>
		api.get('/bridge/inbound/preview', { params }),

	// Token ids the owner holds in a collection (Enumerable or log scan).
	listOwnedTokens: (params: { collection: string; owner: string }) =>
		api.get('/bridge/inbound/owned', { params }),

	// Devnet demo gas drip (server enforces chain blocklist, balance gate,
	// cooldown and drip cap — this never exists on value-bearing chains).
	requestFaucet: (data: { address: string }) => api.post('/bridge/faucet', data),

	// Shadow holder announces a redemption; response carries the custody UA
	// + exact memo. The original NFT is released to the named EVM address.
	prepareRedeem: (data: {
		cid: string
		sender_z_address: string
		evm_recipient: string
	}) => api.post('/bridge/prepare-redeem', data),
}

// Wallet scanner helpers
export const walletScannerApi = {
	getUtxos: (address: string) =>
		api.post('/wallet-scanner/utxos', { address }),
	broadcastRawTransaction: (rawTxHex: string, allowHighFees = false) =>
		api.post('/wallet-scanner/transactions/broadcast', { rawTxHex, allowHighFees })
}

export default api 